import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Input, message, Button, Select, Alert } from 'antd';
import { CopyOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { makePostCall, getUserDetails, calculateExactAge } from '../utils/helper';
import moment from 'moment';
import { FieldLabels } from '../constants/fieldLabels';
import { BASE_API } from '../axios';

const WhatsAppShareModal = ({
  visible,
  onCancel,
  studyUID,
  pacOrdId,
  studyDescription,
  patientName,
  patientMobile,
  patientGender,
  patientDOB,
  studyDate,
  refDoctorName,
  refDoctorContact,
}) => {
  const defaultExpiry = window.ENV_CONFIG?.DEFAULT_LINK_EXPIRY;
  const [loading, setLoading] = useState(false);
  const [patientNumber, setPatientNumber] = useState(patientMobile || '');
  const [doctorName, setDoctorName] = useState(refDoctorName || '');
  const [doctorContact, setDoctorContact] = useState(refDoctorContact || '');
  const [expiryHours, setExpiryHours] = useState( defaultExpiry === null ? null : (defaultExpiry || 48));
  const [showSameNumberWarning, setShowSameNumberWarning] = useState(false);
  const [shareableUrl, setShareableUrl] = useState('');

  const centerName = window.ENV_CONFIG?.CENTER_NAME || 'DIAGNOSTIC CENTER';

  // Check if patient and doctor numbers are the same
  useEffect(() => {
    if (patientNumber && doctorContact && patientNumber.trim() === doctorContact.trim()) {
      setShowSameNumberWarning(true);
    } else {
      setShowSameNumberWarning(false);
    }
  }, [patientNumber, doctorContact]);

  const handleSendWhatsApp = async () => {
    // Validate mobile numbers
    const mobileNumbers = [];

    if (patientNumber) {
      if (!/^[6-9]\d{9}$/.test(patientNumber)) {
        message.error('Please enter a valid patient mobile number');
        return;
      }
      mobileNumbers.push(patientNumber);
    }

    if (doctorContact) {
      if (!/^[6-9]\d{9}$/.test(doctorContact)) {
        message.error('Please enter a valid doctor contact number');
        return;
      }
      mobileNumbers.push(doctorContact);
    }

    if (mobileNumbers.length === 0) {
      message.error('Please provide at least one mobile number');
      return;
    }

    setLoading(true);

    try {
      // Calculate patient age
      const patientAge = patientDOB ? calculateExactAge(patientDOB) : null;
      const formattedStudyDate = studyDate ? moment(studyDate).format('DD-MM-YYYY') : 'N/A';

      const payload = {
        study_uid: studyUID,
        user_id: getUserDetails()?.username,
        study_description: studyDescription || 'N/A',
        expires_in_hours: expiryHours,
        mobile_numbers: mobileNumbers,
        patient_name: patientName || 'Unknown',
        patient_age: patientAge,
        patient_gender: patientGender || 'Unknown',
        study_date: formattedStudyDate,
        pacs_ord_id: pacOrdId,
      };

      const response = await makePostCall('/generate-study-link', payload);

      if (response.data.success) {
        message.success('Study link sent successfully on WhatsApp!');
        onCancel();
      } else {
        message.error('Failed to send study link');
      }
    } catch (error) {
      console.error('Error sending WhatsApp link:', error);
      message.error('Failed to send study link');
    } finally {
      setLoading(false);
    }
  };

  const generateStudyLink = async () => {
    const response = await makePostCall('/generate-study-link', {
      study_uid: studyUID,
      study_description: studyDescription,
      user_id: getUserDetails()?.username,
      expires_in_hours: expiryHours
    });

    if (response.data.success) {
      setShareableUrl(response.data.data.url); // Backend provides full URL
      return response.data.data.url;
    }
  };

  const formatMessageText = (url, forWhatsApp = false) => {
    // Calculate patient age and format study date
    const patientAge = patientDOB ? calculateExactAge(patientDOB) : 'Unknown';
    const formattedStudyDate = studyDate ? moment(studyDate).format('DD-MM-YYYY') : 'Invalid date';

    // Create the message in the required format
    const greeting = 'Dear Sir/Mam,';
    const mainMessage = `${centerName} have shared you the digital images of ${patientName} ${patientAge}/${patientGender} done on ${formattedStudyDate} via Photon PACS`;
    const linkIntro = 'Click on link to view your scan:';

    const lines = [
      greeting,
      '',
      mainMessage,
      '',
      linkIntro,
      '',
      url,
      '',
      'Note: For better viewing experience on mobile, please view in landscape mode.'
    ];

    return lines.join('\n');
  };


  const handleGenerateAndCopy = async () => {
    let urlToUse = shareableUrl;

    if (!urlToUse) {
      urlToUse = await generateStudyLink();
    }

    if (urlToUse) {
      try {
        const message_text = formatMessageText(urlToUse, false);
        await navigator.clipboard.writeText(message_text);
        message.success('Study link with details copied to clipboard!');
        setTimeout(() => {
          onCancel && onCancel();
        }, 200);
      } catch (error) {
        message.error('Link generated but failed to copy to clipboard');
      }
    }
  };

  const expiryOptions = [
    { label: '6 Hours', value: 6 },
    { label: '24 Hours', value: 24 },
    { label: '48 Hours', value: 48 },
    { label: '7 Days', value: 168 },
    { label: 'Never Expire', value: null },
  ];

  return (
    <Modal
      title="Share Study on WhatsApp"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={500}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Study Information:
          </label>
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
            {FieldLabels.PATIENT_NAME}: {patientName || 'Unknown'}
            <br />
            Study: {studyDescription || 'N/A'}
            <br />
            Date: {studyDate ? moment(studyDate).format('DD-MM-YYYY') : 'N/A'}
          </p>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Link Expiry Time:
          </label>
          <Select
            value={expiryHours}
            onChange={setExpiryHours}
            options={expiryOptions}
            style={{ width: '100%' }}
            disabled={loading}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            {FieldLabels.PATIENT_NAME}
          </label>
          <Input
            value={patientName}
            disabled
            placeholder={FieldLabels.PATIENT_NAME}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Patient Mobile Number:
          </label>
          <Input
            value={patientNumber}
            onChange={e => setPatientNumber(e.target.value)}
            placeholder="Enter patient mobile number"
            maxLength={10}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            {FieldLabels.REFERRING_PHYSICIAN}:
          </label>
          <Input
            value={doctorName}
            onChange={e => setDoctorName(e.target.value)}
            placeholder="Enter doctor name"
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Doctor Contact Number:
          </label>
          <Input
            value={doctorContact}
            onChange={e => setDoctorContact(e.target.value)}
            placeholder="Enter doctor contact number"
            maxLength={10}
          />
        </div>

        {showSameNumberWarning && (
          <Alert
            message="Warning: Same Contact Number"
            description="The patient mobile number and doctor contact number are the same. The link will only be sent once to this number."
            type="warning"
            showIcon
            closable
          />
        )}

        <Button
          type="primary"
          icon={<WhatsAppOutlined />}
          onClick={handleSendWhatsApp}
          loading={loading}
          disabled={loading}
          style={{
            backgroundColor: '#25D366',
            borderColor: '#25D366',
            color: 'white',
            height: '40px',
            fontSize: '16px',
          }}
        >
          Send Link on WhatsApp
        </Button>

        <Button
          type="primary"
          icon={<CopyOutlined />}
          onClick={handleGenerateAndCopy}
          loading={loading && !shareableUrl}
          disabled={loading && !shareableUrl}
          style={{ flex: 1 }}
        >
          Generate & Copy Link
        </Button>

        <div
          style={{ background: '#f0f8ff', padding: '12px', borderRadius: '6px', fontSize: '13px' }}
        >
          <strong>Note:</strong>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            <li>
              {expiryHours === null
                ? 'Link will never expire'
                : `Link will expire in ${expiryHours} hours`}
            </li>
            <li>Link will be sent to all provided mobile numbers</li>
            <li>At least one mobile number is required</li>
          </ul>
        </div>
      </div>
    </Modal>
  );
};

export default WhatsAppShareModal;
